import * as amplitude from "@amplitude/analytics-browser";
import { SHARED_EVENTS_PARAMS } from "./shared";

export const CHECKOUT_CUSTOMIZATION_EVENTS = {
  PAGE_VIEWED: "Checkout Settings Page Viewed",
  TAB_VIEWED: "Checkout Settings Tab Viewed",
  BRANDING_FIELD_UPDATED: "Checkout Branding Field Updated",
  PAYMENT_SETTINGS_TOGGLED: "Checkout Payment Setting Toggled",
  PAYMENT_LAYOUT_CHANGED: "Checkout Payment Layout Changed",
  POST_PAYMENT_MODE_CHANGED: "Checkout Post Payment Setting Selected",
  CONFIRMATION_MSG_CHANGED: "Checkout Custom Confirmation Message Updated",
  APPLY_CHANGES_CLICKED: "Checkout Apply Changes Clicked",
  SAVE_CONFIRMED: "Checkout Save Confirmed",
  CHANGES_APPLIED_TOAST_VIEWED: "Checkout Changes Applied Toast Viewed",
  RESET_BTN_CLICKED: "Checkout Reset Clicked",
  RESET_CONFIRMED: "Checkout Reset Confirmed",
  SAVE_FAILED: "Checkout Save Failed"
};

export const CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS = {
  TAB_NAME: "tab_name",
  FIELD_NAME: "field_name",
  SETTING_NAME: "setting_name",
  LAYOUT: "layout",
  CONFIRMATION_OPTION: "confirmation_option",
  CUSTOM_MSG_ENABLED: "custom_message_enabled",
  MSG_LENGTH: "message_length",
  ACTIVE_TAB: "active_tab"
};

export const CHECKOUT_CUSTOMIZATION_EVENTS_VALUES = {
  TABS: {
    BRANDING: "branding",
    PAYMENT: "payment",
    POST_PAYMENT: "post_payment",
  } as const,
  FIELDS_NAMES: {
    BUSINESS_LOGO: "logo",
    ADDRESS: "address",
    CITY: "city",
    PHONE_NUMBER: "phone_number",
    COLORS: "colors",
    FONT: "font",
    STYLE: "style",

    LAYOUT: "layout",

    TOGGLES: {
      SHOW_SAVE_CARD: "show_save_card",
      ALLOW_RETRIES: "allow_retries",
      ALLOW_CANCELLATION: "allow_cancellation",
      SHOW_INSTANT_REFUND: "show_instant_refund",
      SHOW_BILLING_ADDRESS: "show_billing_address",
      SHOW_ITEMS: "show_items",
      SHOW_TERMS: "show_terms_and_conditions",
      SPLIT_PAYMENT: "split_payment",
    },

    AUTO_REDIRECT: "auto_redirect",
    SHOW_BACK_BTN: "show_back_button",
    REDIRECT_IMMEDIATELY: "redirect_immediately",
  } as const,
  SERVER_SIDE_FIELDS_NAMES: {
    MERCHANT_LOGO: "merchant_logo",
  },
  TAB_VIEW: "tab_view",
  LIST_VIEW: "list_view",
};

export const classifiedTrackableFieldsNames: Record<string, string[]> = {
  branding: [
    'merchant_logo',
    'checkout_address',
    'checkout_city',
    'checkout_phone_number',
    'colors',
    'font',
    'style'
  ],
  'payments.toggles': [
    'display_savecard',
    'allow_payments_retries',
    'allow_payment_cancellation',
    'enable_instant_refund',
    'display_items',
    'display_billing',
  ],
  'payments.split_payment': [
    'enabled',
  ],
  'payments': [
    'layout',
    'showTerms',
    'pay_later'
  ],
  post_payment: [
    'mode',
    'useCustomMessage'
  ]
};

const toggleFields = [
  'display_savecard',
  'allow_payments_retries',
  'allow_payment_cancellation',
  'enable_instant_refund',
  'display_items',
  'display_billing',
  'enabled',
  'showTerms',
  'pay_later'
];

export const additionalValuesFields: Record<string, string> = {
  'useCustomMessage': 'post_payment.message'
};

export const trackableFieldsNames = [
  ...classifiedTrackableFieldsNames.branding,
  ...classifiedTrackableFieldsNames['payments.toggles'],
  ...classifiedTrackableFieldsNames['payments.split_payment'],
  ...classifiedTrackableFieldsNames.payments,
  ...classifiedTrackableFieldsNames.post_payment
];

const fieldsNamesMap: Record<(typeof trackableFieldsNames)[number], string> = {
  merchant_logo: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.BUSINESS_LOGO,
  checkout_address: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.ADDRESS,
  checkout_city: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.CITY,
  checkout_phone_number: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.PHONE_NUMBER,
  colors: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.COLORS,
  font: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.FONT,
  style: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.STYLE,

  layout: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.LAYOUT,

  show_save_card: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.SHOW_SAVE_CARD,
  allow_payments_retries: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.ALLOW_RETRIES,
  allow_payment_cancellation: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.ALLOW_CANCELLATION,
  enable_instant_refund: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.SHOW_INSTANT_REFUND,
  display_items: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.SHOW_ITEMS,
  display_billing: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.SHOW_BILLING_ADDRESS,
  showTerms: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.SHOW_TERMS,
  enabled: CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.TOGGLES.SPLIT_PAYMENT,
};

const valuesMap: Record<string, string> = {
  'tab': CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.TAB_VIEW,
  'list': CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.LIST_VIEW,
  'AUTO_REDIRECT': CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.AUTO_REDIRECT,
  'REDIRECT_ON_CLICK': CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.SHOW_BACK_BTN,
  'REDIRECTION_LOADING': CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.FIELDS_NAMES.REDIRECT_IMMEDIATELY,
};

export const trackFieldsUpdate = (
  tab: string,
  fieldName: string,
  value?: string | number | boolean,
  additionalValue?: string
) => {
  tab === 'branding' ?
    amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.BRANDING_FIELD_UPDATED, {
      [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.FIELD_NAME]: fieldsNamesMap[fieldName] || fieldName
    })
    :
    (tab === 'payments.toggles' || toggleFields.includes(fieldName)) ?
      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.PAYMENT_SETTINGS_TOGGLED, {
        [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.SETTING_NAME]: fieldsNamesMap[fieldName] || fieldName,
        [SHARED_EVENTS_PARAMS.ENABLED]: value
      })
      :
      tab === 'payments' && fieldName === 'layout' ?
        amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.PAYMENT_LAYOUT_CHANGED, {
          [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.LAYOUT]: valuesMap[String(value)] || value,
        })
        :
        tab === 'post_payment' &&
        (
          fieldName === 'mode' ?
            amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.POST_PAYMENT_MODE_CHANGED, {
              [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.CONFIRMATION_OPTION]: valuesMap[String(value)] || value,
            })
            :
            fieldName === 'useCustomMessage' &&
            amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.CONFIRMATION_MSG_CHANGED, {
              [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.CUSTOM_MSG_ENABLED]: value,
              ...(
                value
                  ?
                  { [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.MSG_LENGTH]: additionalValue?.length }
                  :
                  {}
              ),
            })
        );
}